import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      const storedToken = localStorage.getItem('token');
      const storedEmail = localStorage.getItem('userEmail');
      
      console.log('Initializing auth with:', { storedToken, storedEmail }); // Debug log
      
      if (storedToken) {
        setToken(storedToken);
      }
      if (storedEmail) {
        setUserEmail(storedEmail);
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      // CRITICAL: Clear any existing token before login
      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      setToken(null);
      setUserEmail(null);
      
      const response = await axios.post('/api/login', { email, password });
      console.log('Login response:', response.data); // Debug log
      
      // Check if user is new and needs to set password
      if (response.data.isNewUser) {
        return {
          success: true,
          isNewUser: true,
          message: response.data.message
        };
      }
      
      const { token: newToken, email: userEmail } = response.data;
      console.log('Setting userEmail:', userEmail); // Debug log
      
      // CRITICAL: Only set new token after successful login
      if (newToken && userEmail) {
        localStorage.setItem('token', newToken);
        localStorage.setItem('userEmail', userEmail);
        
        setToken(newToken);
        setUserEmail(userEmail);
        
        return { success: true, isNewUser: false };
      } else {
        console.error('Login failed: Missing token or email in response');
        return {
          success: false,
          error: 'Invalid login response'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      // Ensure token is cleared on error
      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      setToken(null);
      setUserEmail(null);
      
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
        requiresEmailVerification: error.response?.data?.requiresEmailVerification || false,
        email: error.response?.data?.email
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      await axios.post('/api/register', { email, password, name });
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    setToken(null);
    setUserEmail(null);
  };

  const setAuthFromToken = (newToken, email) => {
    // CRITICAL: Clear any existing token before setting new one
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    setToken(null);
    setUserEmail(null);
    
    // Then set the new token
    if (newToken && email) {
      localStorage.setItem('token', newToken);
      localStorage.setItem('userEmail', email);
      setToken(newToken);
      setUserEmail(email);
    } else {
      console.error('setAuthFromToken: Missing token or email');
    }
  };

  const value = {
    token,
    userEmail,
    login,
    register,
    logout,
    setAuthFromToken,
    loading
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 